import { Collection } from 'common-ts/dist/models/Product';

// Session-level ad attribution for product scoring.
//
// Captured ONCE per browser session, from the LANDING url — must run before the SPA rewrites
// the query string (App.tsx calls getSessionAttribution() at the top of its mount effect,
// ahead of the history.push calls that strip ?fbclid / ?flowversion).
//
// Ads are multi-product video ads landing on ?collectionkey=<collectionId> pages, so the ad
// "target" is a collection: every product in it counts as AD-TARGETED for this session, and
// everything else the visitor browses counts as ORGANIC (they didn't come pre-selected for
// those). Direct-to-PDP ads are the fallback case (adTargetProductId).

export interface SessionAttribution {
  sessionId: string;
  hasAdMarkers: boolean;
  adSource?: string;
  adCollectionKey?: string;
  adTargetProductId?: string;
  landingUrl: string;
}

const STORAGE_KEY = 'sessionAttribution_v1';

const PAID_UTM_MEDIUMS = ['paid', 'cpc', 'ppc', 'paid_social', 'paidsocial'];
const META_UTM_SOURCES = ['fb', 'ig', 'facebook', 'instagram', 'meta'];

let inMemoryFallback: SessionAttribution | null = null;

const buildFromLocation = (): SessionAttribution => {
  const href = window.location.href;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch (e) {
    params = new URLSearchParams('');
  }

  const fbclid = params.get('fbclid');
  const ttclid = params.get('ttclid');
  const gclid = params.get('gclid');
  const utmSource = (params.get('utm_source') || '').toLowerCase();
  const utmMedium = (params.get('utm_medium') || '').toLowerCase();

  const hasAdMarkers =
    !!fbclid || !!ttclid || !!gclid ||
    PAID_UTM_MEDIUMS.indexOf(utmMedium) !== -1 ||
    META_UTM_SOURCES.indexOf(utmSource) !== -1;

  let adSource: string | undefined = undefined;
  if (fbclid || META_UTM_SOURCES.indexOf(utmSource) !== -1) {
    adSource = 'meta';
  } else if (ttclid) {
    adSource = 'tiktok';
  } else if (gclid) {
    adSource = 'google';
  } else if (hasAdMarkers && utmSource) {
    adSource = utmSource.substring(0, 32);
  }

  let adCollectionKey: string | undefined = undefined;
  let adTargetProductId: string | undefined = undefined;
  if (hasAdMarkers) {
    // Primary: collection landing (?collectionkey=... pins that collection to the top of the
    // landing list — see App.tsx / PageProductList).
    const collectionKey = params.get('collectionkey');
    if (collectionKey) {
      adCollectionKey = collectionKey.substring(0, 64);
    }
    // Fallback: direct-to-PDP ad (/shop/product/:productId and route variants).
    const pdpMatch = window.location.pathname.match(/\/product\/([^/?#]+)/);
    if (pdpMatch && pdpMatch[1]) {
      try {
        adTargetProductId = decodeURIComponent(pdpMatch[1]).substring(0, 64);
      } catch (e) {
        adTargetProductId = pdpMatch[1].substring(0, 64);
      }
    }
  }

  return {
    sessionId:
      'SID-' + Date.now().toString(36) + '-' + Math.random().toString(36).substring(2, 10),
    hasAdMarkers,
    adSource,
    adCollectionKey,
    adTargetProductId,
    landingUrl: href.substring(0, 500),
  };
};

// Lazily initializes (and persists) the session record; safe to call from every event.
export const getSessionAttribution = (): SessionAttribution => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed.sessionId === 'string') {
        return parsed as SessionAttribution;
      }
    }
  } catch (e) {
    // sessionStorage unavailable (private mode etc.) -> in-memory fallback below
  }

  if (inMemoryFallback) return inMemoryFallback;

  const attribution = buildFromLocation();
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch (e) {
    inMemoryFallback = attribution;
  }
  return attribution;
};

const isProductAdTargeted = (
  productId: string,
  attribution: SessionAttribution,
  collectionsDictionary: { [key: string]: Collection }
): boolean => {
  if (!attribution.hasAdMarkers) return false;
  if (attribution.adTargetProductId && attribution.adTargetProductId === productId) return true;
  if (attribution.adCollectionKey) {
    const collection = collectionsDictionary[attribution.adCollectionKey];
    if (collection && Array.isArray(collection.productItemsList)) {
      return collection.productItemsList.indexOf(productId) !== -1;
    }
  }
  return false;
};

export const getSegmentForProduct = (
  productId: string,
  collectionsDictionary: { [key: string]: Collection }
): 'AD-TARGETED' | 'ORGANIC' => {
  const attribution = getSessionAttribution();
  return isProductAdTargeted(productId, attribution, collectionsDictionary)
    ? 'AD-TARGETED'
    : 'ORGANIC';
};

// For order submission: which of the cart's products are ad-targeted this session.
// Precomputed client-side so the payment webhook needs no collection-membership lookup.
export const getAdTargetedProductIds = (
  productIds: string[],
  collectionsDictionary: { [key: string]: Collection }
): string[] => {
  const attribution = getSessionAttribution();
  if (!attribution.hasAdMarkers) return [];
  const unique = productIds.filter((id, index) => productIds.indexOf(id) === index);
  return unique
    .filter((id) => isProductAdTargeted(id, attribution, collectionsDictionary))
    .slice(0, 50);
};
